import { BulkOptionType } from 'elements/hardwareSelectorV2/types'
import { Button, Grid, MenuItem, Select, ShopCartIcon, styled, TextField } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { ChangeEvent, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { setIgnoreClickOutside } from 'reducer/globalCart'
import { MAX_QUANTITY } from './QuantityButton'

const GridContainer = styled(Grid)(({ theme }) => ({
  width: '100%',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: 0,
}))

const GridItem = styled(Grid)(() => ({
  padding: '0px !important',
  width: '100%',
}))

const StyledQuantityInput = styled(TextField)(({ theme }) => ({
  '& fieldset': {
    border: 'none',
    borderRadius: 0,
  },
  '& input': {
    textAlign: 'center',
    height: '100%',
    padding: '0px !important',
  },
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const BulkDropdown = styled(Select)(({ theme }) => ({
  '& fieldset': {
    border: 'none !important',
    padding: '0px !important',
    borderLeft: `1px solid ${theme.palette.divider} !important`,
    borderRadius: '0px !important',
  },
  '& .MuiSelect-outlined': {
    padding: '10px !important',
  },
  '& [role="combobox"] > span': {
    width: '100%',
    textAlign: 'center',
  },
  border: 'none !important',
  fontSize: 14,
  backgroundColor: theme.palette.neutralGrey[200],
  width: '100%',
}))

const AddtoCart = styled(Button)(({ theme }) => ({
  background: `${theme.palette.blue[800]} !important`,
  '&:hover': {
    border: 'none',
    background: `${theme.palette.blue[900]} !important`,
    color: `${theme.palette.neutralGrey[100]} !important`,
  },
  '&:disabled': {
    background: `${theme.palette.blue[400]} !important`,
  },
  '& .OSUI-Typography-root': {
    color: `${theme.palette.neutralGrey[100]} !important`,
  },
  color: `${theme.palette.neutralGrey[100]} !important`,
  height: '100%',
  borderRadius: 0,
  borderTopRightRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
  '& svg': {
    flexShrink: 0,
  },
}))

const isQuantityValid = (quantity: number) => {
  return quantity > 0
}

type BulkPriceButtonProps = {
  bulkOptions: BulkOptionType[]
  quantity: number
  setQuantity: (quantity: number) => void
  handleAddToCart?: (quantity?: number, bulkOption?: BulkOptionType | null) => void
  hideAddToCart?: boolean
  showConnectDistributorLink?: boolean
  selectedBulkOption: BulkOptionType | null
  setBulkOption: (bulkOption: BulkOptionType | null) => void
  disabled?: boolean
}

const BulkPriceButton = ({
  bulkOptions,
  quantity,
  setQuantity,
  handleAddToCart: addToCart,
  hideAddToCart,
  showConnectDistributorLink = false,
  selectedBulkOption,
  setBulkOption,
  disabled,
}: BulkPriceButtonProps) => {
  const dispatch = useDispatch()
  const translate = useTranslate()
  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    const value = e.target.value
    if (!value) {
      setQuantity(0)
      return
    }
    const isValidQuantity = /^\d*$/.test(value)

    if (!isValidQuantity) return

    const parsedQuantity = parseInt(value)

    const quantityToUpdate = parsedQuantity <= MAX_QUANTITY ? parsedQuantity : MAX_QUANTITY

    setQuantity(quantityToUpdate * (selectedBulkOption?.quantity_per_item ?? 1))
  }

  const handleAddToCart = (quantity: number, bulkOption: BulkOptionType | null) => {
    if (!isQuantityValid(quantity)) return
    addToCart?.(quantity, bulkOption)
    // Reset the quantity
    setQuantity(selectedBulkOption?.quantity_per_item ?? 1)
  }

  const bulkOptionsHashmap = useMemo(() => new Map(bulkOptions.map((option) => [option.variant_id, option])), [
    bulkOptions,
  ])

  const handleBulkOptionChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    event.stopPropagation()
    let quantityToUpdate = 1
    if (event.target.value === 'individual') {
      quantityToUpdate = quantity / (selectedBulkOption?.quantity_per_item ?? 1)
      setQuantity(quantityToUpdate)
      setBulkOption(null)
    } else {
      const bulkOption = bulkOptionsHashmap.get(event.target.value as string) ?? null
      quantityToUpdate =
        (quantity / (selectedBulkOption?.quantity_per_item ?? 1)) * (bulkOption?.quantity_per_item ?? 1)
      setQuantity(quantityToUpdate)
      setBulkOption(bulkOption)
    }
  }

  return (
    <GridContainer container alignContent="center" justify="center" direction="row">
      <GridItem item xs={2}>
        <StyledQuantityInput
          variant="outlined"
          value={quantity / (selectedBulkOption?.quantity_per_item ?? 1)}
          onChange={handleQuantityChange}
          onClick={(e) => {
            e.stopPropagation()
          }}
          disabled={disabled}
        />
      </GridItem>
      <GridItem item xs={hideAddToCart ? 10 : 8}>
        <BulkDropdown
          variant="outlined"
          renderValue={(value) =>
            !selectedBulkOption ? (
              <span>{translate('Individual')}</span>
            ) : (
              <span>Bulk ({bulkOptionsHashmap.get(value as string)?.quantity_per_item} units)</span>
            )
          }
          SelectDisplayProps={{
            style: {
              textAlign: 'center',
            },
          }}
          value={selectedBulkOption?.variant_id ?? 'individual'}
          onChange={handleBulkOptionChange}
          onClose={(e) => {
            e.stopPropagation()
            dispatch(setIgnoreClickOutside(false))
          }}
          onOpen={(e) => {
            e.stopPropagation()
            dispatch(setIgnoreClickOutside(true))
          }}
          disabled={disabled}
        >
          <MenuItem value="individual">
            {translate('Individual')} (1 {translate('unit')})
          </MenuItem>
          {bulkOptions.map((option) => (
            <MenuItem key={option.variant_id} value={option.variant_id}>
              {translate('Bulk')} ({option.quantity_per_item} {translate('units')})
            </MenuItem>
          ))}
        </BulkDropdown>
      </GridItem>
      {!hideAddToCart && (
        <GridItem item xs={2}>
          <AddtoCart
            fullWidth
            onClick={(e) => {
              e.stopPropagation()
              handleAddToCart(quantity, selectedBulkOption)
            }}
            disabled={showConnectDistributorLink || !isQuantityValid(quantity) || disabled}
          >
            <ShopCartIcon size={20} />
          </AddtoCart>
        </GridItem>
      )}
    </GridContainer>
  )
}

export default BulkPriceButton
